import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import ModaArticoliFilters from "./ModaArticoliFilters";
import ModaArticoliTable from "./ModaArticoliTable";
import ModaArticoliAdvancedFiltersModal from "./ModaArticoliAdvancedFiltersModal";
import {
  MODA_ARTICOLI_COSTI_PERMISSION,
  PREZZI_FILTER_FIELDS,
  PREZZI_FILTER_LABELS,
} from "./prezziFiltersConfig";
import { describeCodiciIncollati, parseCodiciIncollati } from "./codiciIncollatiConfig";
import ModaArticoliBulkAssignModal from "./ModaArticoliBulkAssignModal";
import ModaArticoliFunctionsModal from "./ModaArticoliFunctionsModal";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";

const PER_PAGE_OPTIONS_DESKTOP = [10, 15, 20, 100, 200];
const PER_PAGE_OPTIONS_MOBILE = [10, 15, 20];

const createDefaultFilters = () => ({
  codiceVS: "",
  idPs: "",
  stato: "",
  colore: "",
  aggiornatoDa: "",
  aggiornatoA: "",
  importatoDa: "",
  importatoA: "",
  importRegistrato: "",
  pubblicatoDa: "",
  pubblicatoA: "",
  immaginiCreateDa: "",
  immaginiCreateA: "",
  brand_in: [],
  brand_not_in: [],
  persona_in: [],
  persona_not_in: [],
  reparto_in: [],
  reparto_not_in: [],
  categoria_in: [],
  categoria_not_in: [],
  tipo_in: [],
  tipo_not_in: [],
  sottotipo_in: [],
  sottotipo_not_in: [],
  stagione_in: [],
  stagione_not_in: [],
  tagModa_in: [],
  tagModa_not_in: [],
  annullato: "",
  troppeVarianti: "",
  ha_immagine: "",
  imm_nonsitrova: "",
  ut_import_done: "",
  ut_pubbl_done: "",
  ut_pubbl_pren: "",
  ut_imm_done: "",
  ut_imm_pren: "",
  ut_richiede: "",
  priorityMin: "",
  priorityMax: "",
  stockMin: "",
  stockMax: "",
  stockModaMin: "",
  stockModaMax: "",
  codiciIncollati: [],
  codiciIncollatiTipo: "",
  ...Object.fromEntries(
    PREZZI_FILTER_FIELDS.flatMap((field) => [
      [field.min, ""],
      [field.max, ""],
    ]),
  ),
  hasImmagini: false,
  pubblicato: "",
  visibility: "",
  f_467_attivo_ecom: "",
  deactivation_reason: "",
  testi_importati: "",
  botTextConsolidationStatus: "",
  botImageConsolidationStatus: "",
  botSnapshotCategory_in: [],
  botSnapshotCategory_not_in: [],
  botSnapshotHasImage: "",
  vsArticoliGruppo: "",
  mostra_import_bot: "",
  mostra_export_vig: "",
  destinazioni_in: [],
  destinazioni_not_in: [],
  assignedTo: "",
  mongoFilters: {},
});

export const buildModaArticoliSelectionFilters = ({
  quickSearch = "",
  appliedFilters = {},
  mongoFilters,
} = {}) => {
  const normalizedMongoFilters =
    mongoFilters && typeof mongoFilters === "object"
      ? Object.entries(mongoFilters).reduce((acc, [key, value]) => {
          if (Array.isArray(value)) {
            const cleaned = value.filter(
              (item) => item !== "" && item !== null && item !== undefined
            );
            if (cleaned.length) {
              acc[key] = cleaned;
            }
          } else if (typeof value === "string") {
            const trimmed = value.trim();
            if (trimmed) {
              acc[key] = trimmed;
            }
          } else if (value !== undefined && value !== null && value !== "") {
            acc[key] = value;
          }
          return acc;
        }, {})
      : {};
  const normalizedSearch =
    typeof quickSearch === "string" ? quickSearch.trim() : "";

  return {
    ...(normalizedSearch ? { search: normalizedSearch } : {}),
    ...appliedFilters,
    ...(Object.keys(normalizedMongoFilters).length
      ? { mongoFilters: normalizedMongoFilters }
      : {}),
  };
};

export const buildModaArticoliSelectionQueryParams = (
  selectionFilters = {}
) => {
  const { mongoFilters, ...plainFilters } = selectionFilters;
  return {
    ...plainFilters,
    ...(mongoFilters ? { mongoFilters: JSON.stringify(mongoFilters) } : {}),
  };
};

const pickValue = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate !== undefined && candidate !== null) {
      return candidate;
    }
  }
  return undefined;
};

const parseNumberValue = (value) => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const hasComma = trimmed.includes(",");
  const hasDot = trimmed.includes(".");
  let normalized = trimmed;
  if (hasComma && hasDot) {
    normalized = normalized.replace(/\./g, "").replace(",", ".");
  } else if (hasComma) {
    normalized = normalized.replace(",", ".");
  } else {
    normalized = normalized.replace(/,/g, ".");
  }
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : undefined;
};

const inferHasImagesFromValue = (value) => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (value && typeof value === "object") {
    return Object.keys(value).length > 0;
  }
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) {
      return false;
    }
    try {
      const parsed = JSON.parse(trimmed);
      return inferHasImagesFromValue(parsed);
    } catch {
      const content = trimmed
        .replace(/^\[|\]$/g, "")
        .replace(/,/g, "")
        .trim();
      return content.length > 0;
    }
  }
  return Boolean(value);
};

const normalizeLookupRecords = (payload) => {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
    ? payload
    : [];
  const mapped = list
    .map((item) => {
      if (item == null) {
        return null;
      }
      if (typeof item === "string") {
        const value = item.trim();
        if (!value) {
          return null;
        }
        return { code: value, description: value };
      }
      const code =
        item.code ??
        item.codice ??
        item.cod ??
        item.id ??
        item.value ??
        item.slug ??
        item.key ??
        item.ID;
      const isEmpty =
        code === undefined ||
        code === null ||
        (typeof code === "string" && code.trim() === "");
      if (isEmpty) {
        return { code: "__NULL__", description: "(vuoti)" };
      }
      const description =
        item.description ??
        item.descrizione ??
        item.label ??
        item.nomeDisplay ??
        item.nome_display ??
        item.nome ??
        item.name ??
        item.desc ??
        code;
      return {
        code: String(code).trim(),
        description: String(description ?? code).trim(),
      };
    })
    .filter(Boolean);
  const hasNull = mapped.some((entry) => entry.code === "__NULL__");
  if (hasNull) {
    return [
      { code: "__NULL__", description: "(vuoti)" },
      ...mapped.filter((entry) => entry.code !== "__NULL__"),
    ];
  }
  return mapped;
};

const generateSeasonOptions = () => {
  const options = [];
  const currentYear = new Date().getFullYear();
  const lastYear = (currentYear % 100) + 1;
  for (let yy = 20; yy <= lastYear; yy += 1) {
    const yearStr = yy.toString().padStart(2, "0");
    options.push({ code: `${yearStr}1`, description: `${yearStr}1` });
    options.push({ code: `${yearStr}2`, description: `${yearStr}2` });
  }
  return options;
};

const normalizeModaArticolo = (item) => {
  if (!item || typeof item !== "object") {
    return {};
  }

  const normalized = { ...item };

  const codiceVS = pickValue(
    item.codiceVS,
    item.codice_vs,
    item.codicevs,
    item.cod_vs,
    item.codvs,
    item.codVS
  );
  if (codiceVS !== undefined) {
    normalized.codiceVS = codiceVS;
  }

  const codicePS = pickValue(
    item.codicePS,
    item.codice_ps,
    item.codiceps,
    item.cod_ps,
    item.id_ps
  );
  if (codicePS !== undefined) {
    normalized.codicePS = codicePS;
  }

  const nome = pickValue(
    item.nome,
    item.nomeArticolo,
    item.nome_articolo,
    item.nome_completo,
    item.descrizione,
    item.descrizioneBreve,
    item.descrizione_breve
  );
  if (nome !== undefined) {
    normalized.nome = nome;
  }

  const descrizione = pickValue(
    item.descrizione,
    item.descrizioneBreve,
    item.descrizione_breve,
    item.nome,
    item.nomeArticolo,
    item.nome_articolo
  );
  if (descrizione !== undefined) {
    normalized.descrizione = descrizione;
  }
  const descrizioneBreve = pickValue(
    item.descrizioneBreve,
    item.descrizione_breve,
    item.descBreve,
    item.desc_breve
  );
  if (descrizioneBreve !== undefined) {
    normalized.descrizioneBreve = descrizioneBreve;
  }
  if (normalized.nome === undefined && descrizione !== undefined) {
    normalized.nome = descrizione;
  }

  const brand = pickValue(
    item.brand,
    item.brand_nome,
    item.brandName,
    item.marca
  );
  if (brand !== undefined) {
    normalized.brand = brand;
  }

  const categoria = pickValue(
    item.categoria,
    item.categoria_path,
    item.categoriaPath,
    item.pathCategoria,
    item.percorso_categoria,
    item.categoriaCompleta
  );
  if (categoria !== undefined) {
    normalized.categoria = categoria;
  }

  const stagione = pickValue(
    item.stagione,
    item.season,
    item.stagione_corrente
  );
  if (stagione !== undefined) {
    normalized.stagione = stagione;
  }

  const stato = pickValue(item.stato, item.status, item.stato_articolo);
  if (stato !== undefined) {
    normalized.stato = stato;
  }

  const prezzoRaw = pickValue(
    item.prezzo,
    item.prezzo_listino,
    item.prezzoListino,
    item.price,
    item.prezzo_listino_ivato
  );
  if (prezzoRaw !== undefined) {
    const parsed = parseNumberValue(prezzoRaw);
    normalized.prezzo = parsed !== undefined ? parsed : prezzoRaw;
  }

  const updatedAt = pickValue(
    item.updatedAt,
    item.updated_at,
    item.ultimaModifica,
    item.ultima_modifica,
    item.last_update_at
  );
  if (updatedAt !== undefined) {
    normalized.updatedAt = updatedAt;
  }

  const sourceUpdatedAt = pickValue(
    item.source_updated_at,
    item.sourceUpdatedAt,
    item.sourceupdateat,
    item.sourceUpdated_at,
    item.source_update_at
  );
  if (sourceUpdatedAt !== undefined) {
    normalized.sourceUpdatedAt = sourceUpdatedAt;
  }

  const colore = pickValue(
    item.colore,
    item.nome_colore,
    item.nomeColore,
    item.nome_colore_completo
  );
  if (colore !== undefined) {
    normalized.colore = colore;
    if (normalized.nomeColore === undefined) {
      normalized.nomeColore = colore;
    }
  }

  const taglia = pickValue(item.taglia, item.size, item.misura);
  if (taglia !== undefined) {
    normalized.taglia = taglia;
  }

  const persona = pickValue(
    item.persona,
    item.target_persona,
    item.targetPersona,
    item.target
  );
  if (persona !== undefined) {
    normalized.persona = persona;
  }

  const codiceProduttore = pickValue(
    item.codiceProduttore,
    item.codice_produttore,
    item.codiceproduttore,
    item.codiceProd,
    item.cod_prod,
    item.codice_prodotto,
    item.codProd
  );
  if (codiceProduttore !== undefined) {
    normalized.codiceProduttore = codiceProduttore;
  }

  const codiceModello = pickValue(
    item.codiceModello,
    item.codice_modello,
    item.codicemodello,
    item.codModello,
    item.cod_modello,
    item.codMod,
    item.cod_mod,
    item.codMod
  );
  if (codiceModello !== undefined) {
    normalized.codiceModello = codiceModello;
  }

  const codiceColore = pickValue(
    item.codiceColore,
    item.codice_colore,
    item.codicecolore,
    item.codColore,
    item.cod_colore,
    item.codCol,
    item.cod_col,
    item.codCol
  );
  if (codiceColore !== undefined) {
    normalized.codiceColore = codiceColore;
  }

  const reparto = pickValue(
    item.reparto,
    item.repartoDescrizione,
    item.reparto_descrizione,
    item.department,
    item.nome_reparto
  );
  if (reparto !== undefined) {
    normalized.reparto = reparto;
  }

  const tipoOggetto = pickValue(
    item.tipoOggetto,
    item.tipo_oggetto,
    item.tipo,
    item.tipologia,
    item.tipo_articolo
  );
  if (tipoOggetto !== undefined) {
    normalized.tipoOggetto = tipoOggetto;
  }

  const sottotipo = pickValue(
    item.sottotipo,
    item.sotto_tipo,
    item.subtipo,
    item.subTipo,
    item.subtype,
    item.sottotipo_articolo
  );
  if (sottotipo !== undefined) {
    normalized.sottotipo = sottotipo;
  }

  const hasImagesRaw = pickValue(
    item.hasImmagini,
    item.has_immagini,
    item.ha_immagini,
    item.hasImages,
    item.has_images
  );
  if (hasImagesRaw !== undefined) {
    normalized.hasImmagini = Boolean(hasImagesRaw);
  }
  const immaginiField = pickValue(
    item.immagini,
    item.listaImmagini,
    item.lista_immagini,
    item.images,
    item.imageList,
    item.image_list,
    item.immagine
  );
  if (immaginiField !== undefined) {
    normalized.immagini = immaginiField;
    const hasImagesFromField = inferHasImagesFromValue(immaginiField);
    if (normalized.hasImmagini === undefined) {
      normalized.hasImmagini = hasImagesFromField;
    } else if (!normalized.hasImmagini && hasImagesFromField) {
      normalized.hasImmagini = true;
    }
  }
  if (normalized.hasImmagini === undefined) {
    normalized.hasImmagini = false;
  }

  const idPsRaw = pickValue(item.id_ps, item.idPs, item.idPS, item.idps);
  const idPsParsed = parseNumberValue(idPsRaw);
  if (idPsRaw !== undefined || idPsParsed !== undefined) {
    const value = idPsParsed !== undefined ? idPsParsed : idPsRaw;
    normalized.id_ps = value;
    normalized.idPs = value;
  }

  const pubblicato = pickValue(
    item.pubblicato,
    item.stato_pubblicazione,
    item.publish_status
  );
  if (pubblicato !== undefined) {
    normalized.pubblicato = pubblicato;
  }

  const catPrincipale = pickValue(
    item.cat_principale,
    item.catPrincipale,
    item.categoria_principale,
    item.main_category
  );
  if (catPrincipale !== undefined) {
    normalized.cat_principale = catPrincipale;
    normalized.catPrincipale = catPrincipale;
  }

  const stockRaw = pickValue(
    item.stock,
    item.stock_ps,
    item.stockPrestashop,
    item.stock_ps_totale
  );
  const stockParsed = parseNumberValue(stockRaw);
  if (stockRaw !== undefined || stockParsed !== undefined) {
    normalized.stock = stockParsed !== undefined ? stockParsed : stockRaw;
  }

  const stockModaRaw = pickValue(
    item.stockModa,
    item.stock_moda,
    item.qty_totale
  );
  const stockModaParsed = parseNumberValue(stockModaRaw);
  if (stockModaRaw !== undefined || stockModaParsed !== undefined) {
    normalized.stockModa =
      stockModaParsed !== undefined ? stockModaParsed : stockModaRaw;
  }

  const visibility = pickValue(
    item.visibility,
    item.visibilita,
    item.visibilita_ps,
    item.visibility_ps
  );
  if (visibility !== undefined) {
    normalized.visibility = visibility;
  }

  const haImmagineRaw = pickValue(
    item.ha_immagine,
    item.haImmagine,
    item.has_image,
    item.hasImage,
    item.hasImmagine
  );
  if (haImmagineRaw !== undefined) {
    normalized.ha_immagine = Boolean(
      typeof haImmagineRaw === "string"
        ? haImmagineRaw.trim().toLowerCase() === "true" ||
            haImmagineRaw.trim() === "1"
        : haImmagineRaw
    );
  }

  const pubblSoloSuPs = pickValue(
    item.pubbl_solo_su_ps,
    item.pubblSoloSuPs,
    item.pubblSoloPs
  );
  if (pubblSoloSuPs !== undefined) {
    normalized.pubbl_solo_su_ps = Boolean(
      typeof pubblSoloSuPs === "string"
        ? ["1", "true", "si", "sì", "yes"].includes(
            pubblSoloSuPs.trim().toLowerCase()
          )
        : pubblSoloSuPs
    );
  }

  const immagineNonSiTrovaRaw = pickValue(
    item.imm_nonsitrova,
    item.immNonSiTrova,
    item.imageNotFound
  );
  if (immagineNonSiTrovaRaw !== undefined) {
    normalized.imm_nonsitrova = Boolean(
      typeof immagineNonSiTrovaRaw === "string"
        ? ["1", "true", "si", "sì", "yes"].includes(
            immagineNonSiTrovaRaw.trim().toLowerCase()
          )
        : immagineNonSiTrovaRaw
    );
  }

  const utPubblDone = pickValue(
    item.ut_pubbl_done,
    item.utPubblDone,
    item.ultimoPubblicatore
  );
  if (utPubblDone !== undefined) {
    normalized.ut_pubbl_done = utPubblDone;
  }

  const utImmDone = pickValue(
    item.ut_imm_done,
    item.utImmDone,
    item.ultimoImmagini
  );
  if (utImmDone !== undefined) {
    normalized.ut_imm_done = utImmDone;
  }

  const utPubblPren = pickValue(
    item.ut_pubbl_pren,
    item.utPubblPren,
    item.utentePubblicazionePrenotato
  );
  if (utPubblPren !== undefined) {
    normalized.ut_pubbl_pren = utPubblPren;
  }

  const utImmPren = pickValue(
    item.ut_imm_pren,
    item.utImmPren,
    item.utenteImmaginiPrenotato
  );
  if (utImmPren !== undefined) {
    normalized.ut_imm_pren = utImmPren;
  }

  const utRichiede = pickValue(
    item.ut_richiede,
    item.utRichiede,
    item.utenteRichiedente
  );
  if (utRichiede !== undefined) {
    normalized.ut_richiede = utRichiede;
  }

  const priorityRaw = pickValue(
    item.priority,
    item.priorita,
    item.priorita_operativa
  );
  const priorityParsed = parseNumberValue(priorityRaw);
  if (priorityRaw !== undefined || priorityParsed !== undefined) {
    normalized.priority =
      priorityParsed !== undefined ? priorityParsed : priorityRaw;
  }

  if (normalized.id === undefined) {
    normalized.id =
      pickValue(item.id, item.ID, item.id_articolo, item.idArticolo) ??
      normalized.codiceVS ??
      `${normalized.codiceVS ?? ""}-${normalized.codicePS ?? ""}`;
  }

  return normalized;
};

function ModaArticoliBrowser({
  utente,
  token,
  mobile = false,
  temaColori = "rootTema",
  onOpenProduct = () => {},
  refreshToken = 0,
}) {

  const { buttonClassesWhite } = stilePulsanti;
  const [rows, setRows] = useState([]);
  const [totalRows, setTotalRows] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(15);
  const [sortBy, setSortBy] = useState("priority");
  const [sortDirection, setSortDirection] = useState("desc");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [errorDetails, setErrorDetails] = useState(null);
  const [quickSearch, setQuickSearch] = useState("");
  const [quickSearchInput, setQuickSearchInput] = useState("");
  const [filters, setFilters] = useState(() => createDefaultFilters());
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);
  const [showFunctionsModal, setShowFunctionsModal] = useState(false);
  const [bulkSummary, setBulkSummary] = useState(null);
  const [bulkSummaryLoading, setBulkSummaryLoading] = useState(false);
  const [bulkSummaryError, setBulkSummaryError] = useState("");
  const [bulkAssignSaving, setBulkAssignSaving] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState("");
  const [viewMode, setViewMode] = useState("assigned");
  const [initialViewApplied, setInitialViewApplied] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const restoredStateRef = useRef(false);
  const skipNextPageResetRef = useRef(false);
  const refreshTriggerRef = useRef(refreshToken);
  const dataRequestRef = useRef(null);
  const bulkSummaryRequestRef = useRef(null);
  const statsRequestRef = useRef(null);
  const [lookupOptions, setLookupOptions] = useState({
    brand: [],
    persona: [],
    reparto: [],
    categoria: [],
    tipo: [],
    sottotipo: [],
    botSnapshotCategory: [],
    tagModa: [],
    utenti: [],
  });
  const [datiUlterioriStandard, setDatiUlterioriStandard] = useState({});
  const [siteCategories, setSiteCategories] = useState({ VIG: [], BOT: [] });
  const seasonOptions = useMemo(() => generateSeasonOptions(), []);

  const perPageOptions = mobile
    ? PER_PAGE_OPTIONS_MOBILE
    : PER_PAGE_OPTIONS_DESKTOP;

  useEffect(() => {
    const availableOptions = mobile
      ? PER_PAGE_OPTIONS_MOBILE
      : PER_PAGE_OPTIONS_DESKTOP;
    if (!availableOptions.includes(perPage)) {
      setPerPage(10);
    }
  }, [mobile, perPage]);

  useEffect(() => {
    if (!token) {
      return;
    }
    const controller = new AbortController();
    const endpoints = [
      { key: "brand", url: "/moda/lookup/brand" },
      { key: "persona", url: "/moda/lookup/persona" },
      { key: "reparto", url: "/moda/lookup/reparti" },
      { key: "categoria", url: "/moda/lookup/categorie" },
      { key: "tipo", url: "/moda/lookup/tipi" },
      { key: "sottotipo", url: "/moda/lookup/sottotipi" },
      {
        key: "botSnapshotCategory",
        url: "/moda/lookup/categorie-snapshot-bot",
      },
      {
        key: "utenti",
        url: "/moda/lookup/utenti?attivo=1&bot=0",
      },
      { key: "tagModa", url: "/moda/lookup/tag-moda" },
    ];

    const fetchLookups = async () => {
      try {
        const results = await Promise.all(
          endpoints.map(async ({ key, url }) => {
            try {
              const response = await axios.get(`${apiUrl}${url}`, {
                headers: { Authorization: `Bearer ${token}` },
                signal: controller.signal,
              });
              return { key, data: response?.data };
            } catch (error) {
              if (!controller.signal.aborted) {
                console.error(`Errore nel recupero lookup ${key}:`, error);
              }
              return { key, data: [] };
            }
          })
        );
        setLookupOptions((prev) => {
          const next = { ...prev };
          results.forEach(({ key, data }) => {
            next[key] = normalizeLookupRecords(data);
          });
          return next;
        });
      } catch (error) {
        if (!controller.signal.aborted) {
          console.error("Errore generale nel recupero lookup MODA:", error);
        }
      }
    };

    fetchLookups();

    return () => controller.abort();
  }, [token]);

  useEffect(() => {
    if (!token) {
      setDatiUlterioriStandard({});
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    fetch(`${apiUrl}/prodotti/get/datiUlteriori`, {
      headers: { Authorization: `Bearer ${token}` },
      signal: controller.signal,
    })
      .then((resp) => resp.json())
      .then((data) => {
        if (!cancelled) {
          setDatiUlterioriStandard(data || {});
        }
      })
      .catch((error) => {
        if (cancelled || controller.signal.aborted) {
          return;
        }
        console.error("Errore nel recupero dati ulteriori:", error);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  useEffect(() => {
    if (!token) {
      setSiteCategories({ VIG: [], BOT: [] });
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    Promise.all(
      ["VIG", "BOT"].map(async (site) => {
        const response = await axios.get(
          apiUrl + "/prodotti/categories/site-categories?site=" + site,
          {
            headers: { Authorization: "Bearer " + token },
            signal: controller.signal,
          },
        );
        return [site, Array.isArray(response?.data?.data) ? response.data.data : []];
      }),
    )
      .then((entries) => {
        if (!cancelled) setSiteCategories(Object.fromEntries(entries));
      })
      .catch((error) => {
        if (!cancelled && !controller.signal.aborted) {
          console.error("Errore nel recupero categorie sito:", error);
        }
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  const hasPermissions =
    utente?.permessiAPI?.["update:database"] ||
    utente?.permessiAPI?.["read:database"] ||
    utente?.permessiAPI?.["read:moda_articoli"] ||
    utente?.permessiAPI?.["read:moda:articoli"];

  const currentUserEmail = useMemo(() => {
    if (utente?.email) {
      return utente.email;
    }
    if (utente?.mail) {
      return utente.mail;
    }
    return "";
  }, [utente]);

  const canReadCosti = Boolean(
    utente?.permessiAPI?.[MODA_ARTICOLI_COSTI_PERMISSION],
  );

  const appliedFilters = useMemo(() => {
    return Object.entries(filters).reduce((acc, [key, value]) => {
      if (value === undefined || value === null) {
        return acc;
      }
      if (key === "mongoFilters") {
        return acc;
      }
      if (Array.isArray(value)) {
        const cleaned = value
          .map((item) => String(item).trim())
          .filter((item) => item.length > 0);
        if (cleaned.length > 0) {
          acc[key] = cleaned.join(",");
        }
        return acc;
      }
      if (typeof value === "boolean") {
        if (value) {
          acc[key] = value;
        }
        return acc;
      }
      const trimmed = String(value).trim();
      if (trimmed) {
        const targetKey = key === "codicePS" ? "idPs" : key;
        acc[targetKey] = trimmed;
      }
      return acc;
    }, {});
  }, [filters]);

  const appliedFiltersDisplay = useMemo(() => {
    const labelMap = {
      idPs: "Codice PS",
      brand_eq: "Brand (=)",
      persona_eq: "Persona (=)",
      reparto_eq: "Reparto (=)",
      categoria_eq: "Categoria (=)",
      tipo_eq: "Tipo (=)",
      sottotipo_eq: "Sottotipo (=)",
      stagione_eq: "Stagione (=)",
      brand_in: "Brand (IN)",
      brand_not_in: "Brand (NOT IN)",
      persona_in: "Persona (IN)",
      persona_not_in: "Persona (NOT IN)",
      reparto_in: "Reparto (IN)",
      reparto_not_in: "Reparto (NOT IN)",
      categoria_in: "Categoria (IN)",
      categoria_not_in: "Categoria (NOT IN)",
      tipo_in: "Tipo (IN)",
      tipo_not_in: "Tipo (NOT IN)",
      sottotipo_in: "Sottotipo (IN)",
      sottotipo_not_in: "Sottotipo (NOT IN)",
      stagione_in: "Stagione (IN)",
      stagione_not_in: "Stagione (NOT IN)",
      annullato: "Annullato",
      troppeVarianti: "Troppe varianti",
      ha_immagine: "Ha immagine",
      imm_nonsitrova: "Immagine non si trova",
      stato: "Stato",
      colore: "Colore",
      aggiornatoDa: "Aggiornato da",
      aggiornatoA: "Aggiornato a",
      ut_import_done: "Ultimo import da",
      importatoDa: "Data import da",
      importatoA: "Data import a",
      importRegistrato: "Import registrato",
      ut_pubbl_done: "Ultimo pubblicatore",
      ut_pubbl_pren: "Pubblicazione prenotata",
      ut_imm_done: "Ultimo immagini",
      ut_imm_pren: "Immagini prenotate",
      ut_richiede: "Utente richiedente",
      priorityMin: "Priorità >=",
      priorityMax: "Priorità <=",
      stockMin: "Stock web >=",
      stockMax: "Stock web <=",
      stockModaMin: "Stock moda >=",
      stockModaMax: "Stock moda <=",
      pubblicato: "Pubblicato",
      visibility: "Visibilità",
      hasImmagini: "Solo con immagini",
      assignedTo: "Assegnato a",
      botTextConsolidationStatus: "Consolidamento dati/testi BOT",
      botImageConsolidationStatus: "Consolidamento immagini BOT",
      botSnapshotCategory_in: "Cat ps BOT al 31/07/2026 (IN)",
      botSnapshotCategory_not_in: "Cat ps BOT al 31/07/2026 (NOT IN)",
      botSnapshotHasImage: "Immagine associata 31/07/2026",
      vsArticoliGruppo: "Gruppo articolo VS",
      mostra_import_bot: "Da importare da BOT",
      mostra_export_vig: "Da esportare a BOT",
      destinazioni_in: "Destinazioni (IN)",
      destinazioni_not_in: "Destinazioni (NOT IN)",
      ...PREZZI_FILTER_LABELS,
      tagModa_in: "Tag Moda (IN)",
      tagModa_not_in: "Tag Moda (NOT IN)",
    };
    const mongoLabelMap = {
      categoriaPredefinita: "Categoria web predefinita VIG",
      categorieWeb: "Categorie web VIG",
      categoriaBotteroPredefinita: "Categoria web predefinita BOT",
      categorieWebBottero: "Categorie web BOT",
      persona: "Persona",
      stagioneCollezione: "Stagione/collezione",
      stagioneConsigliata: "Stagione consigliata",
      utilizzo: "Utilizzo",
      attivoSuEcom: "Attivo su e-commerce",
      exportAmazon: "Export su Amazon",
      continuativo: "Continuativo",
      metodoSpedizione: "Metodo spedizione",
      personalizzato: "Articolo Personalizzato",
    };
    const consolidationLabels = {
      NOT_ANALYZED: "Mai analizzato",
      PENDING: "Da consolidare",
      PARTIAL: "Parziale",
      CONSOLIDATED: "Consolidato",
      BLOCKED: "Bloccato",
      ERROR: "Errore",
      NOT_APPLICABLE: "Non applicabile",
    };
    const booleanFilterKeys = new Set([
      "annullato",
      "troppeVarianti",
      "ha_immagine",
      "imm_nonsitrova",
      "mostra_import_bot",
      "mostra_export_vig",
    ]);
    const result = Object.entries(appliedFilters).reduce((acc, [key, value]) => {
      const label = labelMap[key] ?? key;
      if (key === "codiciIncollatiTipo") {
        return acc;
      }
      if (key === "tagModa_in" || key === "tagModa_not_in") {
        const descriptions = new Map(
          (lookupOptions.tagModa ?? []).map((tag) => [tag.code, tag.description]),
        );
        acc[label] = String(value)
          .split(",")
          .map((code) => descriptions.get(code) || code)
          .join(", ");
        return acc;
      }
      if (key === "codiciIncollati") {
        // Mai l'elenco nel riepilogo: solo quanti e di che tipo.
        acc["Codici incollati"] = describeCodiciIncollati(
          parseCodiciIncollati(value).codes,
          appliedFilters.codiciIncollatiTipo,
        );
        return acc;
      }
      if (["botTextConsolidationStatus", "botImageConsolidationStatus"].includes(key)) {
        acc[label] = consolidationLabels[value] || value;
      } else if (typeof value === "string" && value.includes(",")) {
        acc[label] = value.split(",").join(", ");
      } else if (booleanFilterKeys.has(key)) {
        if (
          value === true ||
          value === 1 ||
          (typeof value === "string" && value.trim() === "1")
        ) {
          acc[label] = "Sì";
        } else if (
          value === false ||
          value === 0 ||
          (typeof value === "string" && value.trim() === "0")
        ) {
          acc[label] = "No";
        } else if (
          typeof value === "string" &&
          value.trim() &&
          value !== "__NULL__"
        ) {
          acc[label] = value;
        }
      } else if (value === true) {
        acc[label] = "Sì";
      } else {
        acc[label] = value;
      }
      return acc;
    }, {});
    const mongoFilters =
      filters && typeof filters === "object" ? filters.mongoFilters : null;
    if (mongoFilters && typeof mongoFilters === "object") {
      Object.entries(mongoFilters).forEach(([mongoKey, mongoValue]) => {
        if (!Array.isArray(mongoValue) || mongoValue.length === 0) {
          return;
        }
        const isNotIn = mongoKey.endsWith("_not_in");
        const baseKey = mongoKey.replace(/_(in|not_in)$/, "");
        const labelBase = mongoLabelMap[baseKey] ?? baseKey;
        const label = `${labelBase} (${isNotIn ? "NOT IN" : "IN"})`;
        result[label] = mongoValue.join(", ");
      });
    }
    return result;
  }, [appliedFilters, filters, lookupOptions.tagModa]);

  const selectionFilters = useMemo(
    () =>
      buildModaArticoliSelectionFilters({
        quickSearch,
        appliedFilters,
        mongoFilters: filters?.mongoFilters,
      }),
    [quickSearch, appliedFilters, filters]
  );

  const selectionQueryParams = useMemo(
    () => buildModaArticoliSelectionQueryParams(selectionFilters),
    [selectionFilters]
  );

  const applyDefaultSort = useCallback(() => {
    setSortBy("priority");
    setSortDirection("desc");
  }, []);

  const resetSearchInputs = useCallback(() => {
    setQuickSearch("");
    setQuickSearchInput("");
  }, []);

  const showAssignedView = useCallback(() => {
    if (!currentUserEmail) {
      return;
    }
    const nextFilters = {
      ...createDefaultFilters(),
      assignedTo: currentUserEmail,
    };
    setFilters(nextFilters);
    resetSearchInputs();
    applyDefaultSort();
    setViewMode("assigned");
  }, [applyDefaultSort, currentUserEmail, resetSearchInputs]);

  const showNeedTextsView = useCallback(() => {
    const nextFilters = {
      ...createDefaultFilters(),
      pubblicato: "NO",
      ut_pubbl_pren: "__NULL__",
      stockMin: "1",
    };
    setFilters(nextFilters);
    resetSearchInputs();
    applyDefaultSort();
    setViewMode("needTexts");
  }, [applyDefaultSort, resetSearchInputs]);

  const showNeedImagesView = useCallback(() => {
    const nextFilters = {
      ...createDefaultFilters(),
      ha_immagine: "0",
      ut_imm_pren: "__NULL__",
    };
    setFilters(nextFilters);
    resetSearchInputs();
    applyDefaultSort();
    setViewMode("needImages");
  }, [applyDefaultSort, resetSearchInputs]);

  const showAllProductsView = useCallback(() => {
    setFilters(createDefaultFilters());
    resetSearchInputs();
    applyDefaultSort();
    setViewMode("all");
  }, [applyDefaultSort, resetSearchInputs]);

  useEffect(() => {
    if (!initialViewApplied && currentUserEmail && !restoredStateRef.current) {
      showAssignedView();
      setInitialViewApplied(true);
    }
  }, [initialViewApplied, currentUserEmail, showAssignedView]);

  const assignmentSortFn = useCallback((a, b) => {
    const priorityA = Number(a.priority) || 0;
    const priorityB = Number(b.priority) || 0;
    if (priorityA !== priorityB) {
      return priorityB - priorityA;
    }
    const brandA = (a.brand ?? "").toString().toUpperCase();
    const brandB = (b.brand ?? "").toString().toUpperCase();
    if (brandA < brandB) return -1;
    if (brandA > brandB) return 1;
    const stockA = Number(a.stock) || 0;
    const stockB = Number(b.stock) || 0;
    if (stockA !== stockB) {
      return stockB - stockA;
    }
    const codiceA = (
      a.codiceVS ??
      a.codice_vs ??
      a.cod_vs ??
      a.codicevs ??
      ""
    ).toString();
    const codiceB = (
      b.codiceVS ??
      b.codice_vs ??
      b.cod_vs ??
      b.codicevs ??
      ""
    ).toString();
    if (codiceA < codiceB) return -1;
    if (codiceA > codiceB) return 1;
    return 0;
  }, []);

  const fetchData = useCallback(async () => {
    dataRequestRef.current?.abort();
    if (!token) {
      return;
    }
    if (viewMode === "assigned" && !currentUserEmail) {
      return;
    }
    const controller = new AbortController();
    dataRequestRef.current = controller;
    setLoading(true);
    setError("");
    setErrorDetails(null);

    try {
      const {
        assignedTo: assignedFilter,
        ...otherFilters
      } = selectionQueryParams;
      const params = {
        page,
        perPage,
        sortBy,
        sortDirection,
        ...otherFilters,
        ...(assignedFilter ? { assignedTo: assignedFilter } : {}),
      };

      const shouldUseAssignedFallback =
        viewMode === "assigned" && currentUserEmail && !assignedFilter;

      if (viewMode === "assigned" && currentUserEmail) {
        params.assignedTo = assignedFilter ?? currentUserEmail;
      }

      if (shouldUseAssignedFallback) {
        const baseParams = {
          ...params,
        };
        delete baseParams.assignedTo;
        baseParams.page = 1;
        baseParams.perPage = Math.max(perPage, 200);

        const requests = [
          axios.get(`${apiUrl}/moda/articoli`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            params: {
              ...baseParams,
              ut_pubbl_pren: currentUserEmail,
            },
          }),
          axios.get(`${apiUrl}/moda/articoli`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal,
            params: {
              ...baseParams,
              ut_imm_pren: currentUserEmail,
            },
          }),
        ];

        const results = await Promise.allSettled(requests);
        if (
          controller.signal.aborted ||
          dataRequestRef.current !== controller
        ) {
          return;
        }
        let fallbackRows = [];
        const seen = new Set();
        let anySuccess = false;
        let fallbackError = null;

        results.forEach((result) => {
          if (result.status === "fulfilled") {
            anySuccess = true;
            const payload = result.value?.data ?? {};
            const lista = payload.data ?? payload.items ?? [];
            lista.forEach((item) => {
              const normalized = normalizeModaArticolo(item);
              const key =
                normalized.codiceVS ??
                normalized.codice_vs ??
                normalized.codicevs ??
                normalized.cod_vs ??
                normalized.id;
              if (!key || !seen.has(key)) {
                if (key) {
                  seen.add(key);
                }
                fallbackRows.push(normalized);
              }
            });
          } else if (!fallbackError) {
            fallbackError = result.reason;
          }
        });

        if (!anySuccess) {
          throw fallbackError ?? new Error("Impossibile recuperare i dati");
        }

        fallbackRows.sort(assignmentSortFn);
        const start = (page - 1) * perPage;
        const paginatedRows = fallbackRows.slice(start, start + perPage);
        setRows(paginatedRows);
        setTotalRows(fallbackRows.length);
        setWarnings([]);
        setError("");
        setErrorDetails(null);
        return;
      }

      const response = await axios.get(`${apiUrl}/moda/articoli`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        signal: controller.signal,
        params,
      });
      if (
        controller.signal.aborted ||
        dataRequestRef.current !== controller
      ) {
        return;
      }

      const payload = response?.data ?? {};
      const lista = payload.data ?? payload.items ?? [];
      const meta = payload.meta ?? {};
      const totale =
        meta.total ?? payload.total ?? payload.count ?? lista.length;

      if (payload.success === false) {
        setRows([]);
        setTotalRows(0);
        setError(payload.error ?? "Impossibile recuperare i dati da MODA.");
        setErrorDetails(payload.details ?? null);
        setWarnings(Array.isArray(meta.warnings) ? meta.warnings : []);
        return;
      }

      const normalizedRows = Array.isArray(lista)
        ? lista.map(normalizeModaArticolo)
        : [];
      setRows(normalizedRows);
      setTotalRows(Number.isFinite(totale) ? totale : lista.length);
      const metaPage = Number(meta.page);
      if (Number.isFinite(metaPage) && metaPage > 0 && metaPage !== page) {
        setPage(metaPage);
      }
      const metaPerPage = Number(meta.perPage ?? meta.per_page);
      if (
        Number.isFinite(metaPerPage) &&
        metaPerPage > 0 &&
        metaPerPage !== perPage
      ) {
        setPerPage(metaPerPage);
      }
      if (meta.sort) {
        if (meta.sort.by) {
          setSortBy(meta.sort.by);
        }
        if (meta.sort.direction) {
          setSortDirection(meta.sort.direction);
        }
      }
      setWarnings(Array.isArray(meta.warnings) ? meta.warnings : []);
    } catch (err) {
      if (controller.signal.aborted || axios.isCancel(err)) {
        return;
      }
      console.error("Errore nel caricamento MODA articoli", err);
      const status = err?.response?.status;
      const payload = err?.response?.data ?? {};
      if (status === 400) {
        setError(
          payload.error ?? "Richiesta non valida. Controlla i filtri impostati."
        );
        setErrorDetails(payload.details ?? null);
      } else {
        setError(
          payload.error ??
            err?.message ??
            "Impossibile recuperare i dati da MODA."
        );
      }
      setWarnings([]);
    } finally {
      if (dataRequestRef.current === controller) {
        dataRequestRef.current = null;
        setLoading(false);
      }
    }
  }, [
    token,
    page,
    perPage,
    sortBy,
    sortDirection,
    selectionQueryParams,
    viewMode,
    currentUserEmail,
    assignmentSortFn,
  ]);

  useEffect(() => {
    if (refreshTriggerRef.current !== refreshToken) {
      refreshTriggerRef.current = refreshToken;
      fetchData();
    }
  }, [refreshToken, fetchData]);

  useEffect(() => {
    if (skipNextPageResetRef.current) {
      skipNextPageResetRef.current = false;
      return;
    }
    setPage(1);
  }, [perPage, sortBy, sortDirection, quickSearch, appliedFilters]);

  useEffect(() => {
    if (!initialViewApplied && currentUserEmail) {
      return;
    }
    fetchData();
  }, [fetchData, initialViewApplied, currentUserEmail]);

  const handleQuickSearchChange = (value) => {
    setQuickSearchInput(value);
  };

  const handleQuickSearchSubmit = () => {
    setQuickSearch(quickSearchInput);
    setPage(1);
  };

  const handleResetFilters = () => {
    showAllProductsView();
    setError("");
    setErrorDetails(null);
    setPage(1);
  };

  const handleApplyAdvancedFilters = (newFilters) => {
    const merged = { ...createDefaultFilters(), ...newFilters };
    Object.keys(merged).forEach((key) => {
      const value = merged[key];
      if (Array.isArray(value)) {
        merged[key] = value.filter((item) => item !== "");
      }
    });
    setFilters(merged);
    setPage(1);
    setViewMode("custom");
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (newPerPage, newPage) => {
    setPerPage(newPerPage);
    setPage(newPage ?? 1);
  };

  const handleSort = (column, direction) => {
    if (!column) {
      return;
    }
    setSortBy(column);
    setSortDirection(direction);
  };

  const canBulkAssign = Boolean(
    utente?.permessiAPI?.["update:assegnazioniPubblicazione"]
  );

  const fetchBulkSummary = useCallback(async () => {
    bulkSummaryRequestRef.current?.abort();
    if (!token || !canBulkAssign) {
      return;
    }
    const controller = new AbortController();
    bulkSummaryRequestRef.current = controller;
    setBulkSummaryLoading(true);
    setBulkSummaryError("");
    try {
      const response = await axios.get(
        `${apiUrl}/moda/articoli/assignments/summary`,
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
          params: selectionQueryParams,
        }
      );
      if (
        controller.signal.aborted ||
        bulkSummaryRequestRef.current !== controller
      ) {
        return;
      }
      const payload = response?.data ?? {};
      if (payload.success === false) {
        throw new Error(
          payload.error ?? "Impossibile recuperare il riepilogo."
        );
      }
      const data = payload.data ?? {};
      setBulkSummary({
        total:
          data.total ?? payload.total ?? payload.meta?.total ?? data.count ?? 0,
        needsTexts: data.needsTexts ?? data.needs_texts ?? 0,
        needsImages: data.needsImages ?? data.needs_images ?? 0,
        users: Array.isArray(data.users)
          ? data.users
          : Array.isArray(data.userLoads)
          ? data.userLoads
          : [],
      });
    } catch (error) {
      if (controller.signal.aborted || axios.isCancel(error)) {
        return;
      }
      console.error("Errore riepilogo assegnazioni", error);
      const message =
        error?.response?.data?.error ?? error.message ?? "Errore sconosciuto.";
      setBulkSummaryError(message);
    } finally {
      if (bulkSummaryRequestRef.current === controller) {
        bulkSummaryRequestRef.current = null;
        setBulkSummaryLoading(false);
      }
    }
  }, [token, canBulkAssign, selectionQueryParams]);

  const fetchStats = useCallback(async () => {
    statsRequestRef.current?.abort();
    if (!token) {
      return;
    }
    const controller = new AbortController();
    statsRequestRef.current = controller;
    setStatsLoading(true);
    setStatsError("");
    try {
      const response = await axios.get(`${apiUrl}/moda/articoli/stats`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
        params: selectionQueryParams,
      });
      if (
        controller.signal.aborted ||
        statsRequestRef.current !== controller
      ) {
        return;
      }
      const payload = response?.data ?? {};
      if (payload.success === false) {
        throw new Error(
          payload.error ?? "Impossibile recuperare le statistiche."
        );
      }
      setStatsData(payload.data ?? null);
    } catch (error) {
      if (controller.signal.aborted || axios.isCancel(error)) {
        return;
      }
      console.error("Errore statistiche articoli", error);
      const message =
        error?.response?.data?.error ??
        error.message ??
        "Impossibile recuperare le statistiche.";
      setStatsError(message);
    } finally {
      if (statsRequestRef.current === controller) {
        statsRequestRef.current = null;
        setStatsLoading(false);
      }
    }
  }, [token, selectionQueryParams]);

  const handleBulkAssignSubmit = async ({ action, scope, user }) => {
    if (!token || !canBulkAssign) {
      return;
    }
    setBulkAssignSaving(true);
    setBulkSummaryError("");
    try {
      const payload = {
        filters: selectionFilters,
        action,
        scope,
      };
      if (user) {
        payload.user = user;
      }
      await axios.post(
        `${apiUrl}/moda/articoli/assignments/bulk`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setShowBulkAssignModal(false);
      setBulkSummary(null);
      fetchData();
    } catch (error) {
      console.error("Errore assegnazione massiva", error);
      const message =
        error?.response?.data?.error ??
        error.message ??
        "Impossibile completare le assegnazioni.";
      setBulkSummaryError(message);
    } finally {
      setBulkAssignSaving(false);
    }
  };

  useEffect(() => {
    if (showBulkAssignModal) {
      fetchBulkSummary();
      return;
    }
    bulkSummaryRequestRef.current?.abort();
  }, [showBulkAssignModal, fetchBulkSummary]);

  useEffect(
    () => () => {
      dataRequestRef.current?.abort();
      bulkSummaryRequestRef.current?.abort();
      statsRequestRef.current?.abort();
      dataRequestRef.current = null;
      bulkSummaryRequestRef.current = null;
      statsRequestRef.current = null;
    },
    []
  );


  const viewTitles = useMemo(
    () => ({
      assigned: "Assegnati a me",
      needTexts: "Prodotti da fare (Testi)",
      needImages: "Prodotti da fare (Immagini)",
      all: "Tutti i prodotti",
      custom: "MODA articoli",
    }),
    []
  );
  const currentViewTitle = viewTitles[viewMode] ?? viewTitles.assigned;

  const handleOpenProduct = useCallback(
    ({ codVS, row }) => {
      if (!codVS) {
        return;
      }
      const rawPayload = row?.raw_payload ?? row?.rawPayload ?? null;
      onOpenProduct?.({ codVS, row, rawPayload });
    },
    [onOpenProduct]
  );

  if (!hasPermissions) {
    return (
      <div className="pa4">
        <div className="sfondoBiancoTema pa4 br3 shadow-4">
          <h2 className="mt0">Accesso non autorizzato</h2>
          <p>
            Non hai i permessi necessari per visualizzare la tabella
            MODA_articoli.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${temaColori} pa2 pa3-ns contentSubHeader`}>
      <div className="sfondoBiancoTema br3 shadow-4 pa3">
        <ModaArticoliFilters
          quickSearch={quickSearchInput}
          onQuickSearchChange={handleQuickSearchChange}
          onQuickSearchSubmit={handleQuickSearchSubmit}
          onResetFilters={handleResetFilters}
          onOpenAdvancedFilters={() => setShowAdvancedFilters(true)}
          onOpenBulkAssign={() => setShowBulkAssignModal(true)}
          onOpenFunctions={() => setShowFunctionsModal(true)}
          onRefresh={fetchData}
          appliedFilters={appliedFiltersDisplay}
          mobile={mobile}
          onShowAssigned={showAssignedView}
          onShowNeedTexts={showNeedTextsView}
          onShowNeedImages={showNeedImagesView}
          loading={loading}
          canBulkAssign={canBulkAssign}
        />

        {error && (
          <div className="mv3 pa3 br2 bg-washed-red dark-red">
            <div>
              <strong>Errore:</strong> {error}
            </div>
            {errorDetails && (
              <ul className="mt2 mb0 pl3">
                {Object.entries(errorDetails).map(([key, value]) => (
                  <li key={key}>
                    <strong>{key}:</strong> {value}
                  </li>
                ))}
              </ul>
            )}
            {!errorDetails && (
              <button
                type="button"
                className="mt2 button-reset bn pointer underline"
                onClick={fetchData}
              >
                Riprova
              </button>
            )}
          </div>
        )}
        {warnings.length > 0 && (
          <div className="mv3 pa3 br2 bg-washed-yellow dark-gray">
            <div className="b">Attenzione</div>
            <ul className="mt2 mb0 pl3">
              {warnings.map((warning, index) => (
                <li key={`${warning}-${index}`}>{warning}</li>
              ))}
            </ul>
          </div>
        )}

        <ModaArticoliTable
          data={rows}
          loading={loading}
          totalRows={totalRows}
          page={page}
          perPage={perPage}
          perPageOptions={perPageOptions}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
          onSort={handleSort}
          currentUserEmail={
            utente?.email ?? utente?.mail ?? utente?.username ?? ""
          }
          title={currentViewTitle}
          noDataComponent={
            !loading && viewMode === "assigned" ? (
              <div className="tc pa4">
                <p className="f5">
                  non ci sono prodotti assegnati a te al momento
                </p>
                <div className="mt3 flex flex-wrap justify-center">
                  <button
                    type="button"
                    className={`${buttonClassesWhite} mh2 mv2`}
                    onClick={showNeedTextsView}
                  >
                    Visualizza prodotti di cui fare testi
                  </button>
                  <button
                    type="button"
                    className={`${buttonClassesWhite} mh2 mv2`}
                    onClick={showNeedImagesView}
                  >
                    Visualizza prodotti di cui fare immagini
                  </button>
                  <button
                    type="button"
                    className={`${buttonClassesWhite} mh2 mv2`}
                    onClick={showAllProductsView}
                  >
                    Visualizza tutti i prodotti
                  </button>
                </div>
              </div>
            ) : undefined
          }
          onOpenProduct={handleOpenProduct}
        />
      </div>

      <ModaArticoliAdvancedFiltersModal
        open={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApply={handleApplyAdvancedFilters}
        initialFilters={filters}
        lookupOptions={lookupOptions}
        stagioneOptions={seasonOptions}
        datiUlterioriStandard={datiUlterioriStandard}
        siteCategories={siteCategories}
        temaColori={temaColori}
        canReadCosti={canReadCosti}
      />
      {showFunctionsModal && (
        <ModaArticoliFunctionsModal
          open={showFunctionsModal}
          onClose={() => setShowFunctionsModal(false)}
          totalRows={totalRows}
          token={token}
          utente={utente}
          datiUlterioriStandard={datiUlterioriStandard}
          siteCategories={siteCategories}
          selectionFilters={selectionFilters}
          sortBy={sortBy}
          sortDirection={sortDirection}
          stats={statsData}
          statsLoading={statsLoading}
          statsError={statsError}
          onRefreshStats={fetchStats}
          onRefresh={fetchData}
          temaColori={temaColori}
        />
      )}
      {canBulkAssign && (
        <ModaArticoliBulkAssignModal
          open={showBulkAssignModal}
          onClose={() => setShowBulkAssignModal(false)}
          summary={bulkSummary}
          loading={bulkSummaryLoading}
          error={bulkSummaryError}
          onRefresh={fetchBulkSummary}
          userOptions={lookupOptions.utenti}
          onSubmit={handleBulkAssignSubmit}
          saving={bulkAssignSaving}
        />
      )}
    </div>
  );
}

export default ModaArticoliBrowser;
